// /app/frontend/src/pages/CommunitiesPage.jsx
import React, { useMemo, useState } from "react";
import { COMMUNITY_REGIONS, COMMUNITY_NOTE } from "../data/communities";
import { useNavigate } from "react-router-dom";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";
import { Info } from "lucide-react";
import InlineNotice from "../components/common/InlineNotice";

function normalize(s) {
  return (s || "").toLowerCase().replace(/[\u2019']/g, "'").trim();
}

export default function CommunitiesPage() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const query = normalize(q);

  const filtered = useMemo(() => {
    if (!query) return COMMUNITY_REGIONS;

    return COMMUNITY_REGIONS.map((r) => {
      const matchesRegion = normalize(r.title).includes(query) || normalize(r.blurb).includes(query);
      const countries = r.countries.filter((c) => normalize(c).includes(query));
      if (matchesRegion) return r;
      return { ...r, countries };
    }).filter((r) => r.countries.length > 0 || normalize(r.title).includes(query));
  }, [query]);

  const totalCountries = useMemo(() => {
    return COMMUNITY_REGIONS.reduce((sum, r) => sum + (r.countries?.length || 0), 0);
  }, []);

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-5xl mx-auto px-4 py-10">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">Communities & Regions</h1>
            <p className="mt-2 text-white/70 max-w-2xl">
              UnityLove is built for the African diaspora worldwide — across Africa, the Caribbean, the Americas, and beyond.
              This page makes that explicit on day one.
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="shrink-0 px-4 py-2 rounded-xl bg-white/10 hover:bg-white/15 text-white/90"
          >
            ← Back
          </button>
        </div>

        <div className="mt-6 mb-4">
          <InlineNotice variant="info" title="Worldwide inclusion" testId="communities-inclusion-note">
            This list helps us plan themed nights and regional scheduling. It's never used to exclude anyone.
          </InlineNotice>
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/5 p-4 sm:p-5">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
            <div className="flex-1">
              <label className="block text-sm text-white/70 mb-1">Search a country or region</label>
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Try: Colombia, Nigeria, Jamaica, Brazil, UK…"
                className="w-full px-4 py-3 rounded-2xl bg-black/40 border border-white/15 focus:outline-none focus:ring-2 focus:ring-pink-500/40"
                data-testid="communities-search-input"
              />
            </div>
            <div className="text-sm text-white/60">
              <div className="font-medium text-white/80">{totalCountries}+ locations listed</div>
              <div>and still expanding</div>
            </div>
          </div>

          <div className="mt-4 text-sm text-white/70">
            <span className="text-white/90 font-medium">{COMMUNITY_NOTE.title}:</span> {COMMUNITY_NOTE.body}
          </div>
        </div>

        <div className="mt-8 space-y-4" data-testid="communities-regions-list">
          {filtered.map((r) => (
            <details key={r.key} className="group rounded-2xl border border-white/10 bg-white/5">
              <summary className="cursor-pointer list-none px-5 py-4 flex items-center justify-between">
                <div>
                  <div className="text-lg font-semibold">{r.title}</div>
                  <div className="text-sm text-white/60 mt-1">{r.blurb}</div>
                </div>
                <div className="text-white/60 group-open:rotate-180 transition-transform">⌄</div>
              </summary>

              <div className="px-5 pb-5">
                <div className="flex flex-wrap gap-2">
                  {(r.countries || []).map((c) => (
                    <span
                      key={c}
                      className="text-sm px-3 py-1.5 rounded-full bg-black/35 border border-white/10 text-white/85"
                    >
                      {c}
                    </span>
                  ))}
                </div>

                {(r.countries || []).length === 0 && (
                  <div className="text-sm text-white/60">
                    No matches in this region. Try a different search.
                  </div>
                )}
              </div>
            </details>
          ))}

          {filtered.length === 0 && (
            <div className="rounded-2xl border border-white/10 bg-white/5 p-6 text-white/70">
              No matches found. You're still welcome here — regional events will expand as the community grows.
            </div>
          )}
        </div>

        <div className="mt-10 rounded-2xl border border-white/10 bg-white/5 p-6">
          <div className="text-lg font-semibold">Don't see yourself?</div>
          <p className="mt-2 text-white/70">
            You're still included. This list is for{" "}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="underline decoration-dotted underline-offset-2 cursor-help inline-flex items-center gap-1">
                    visibility and regional scheduling
                    <Info className="inline w-3.5 h-3.5 text-white/50" />
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs bg-zinc-800 text-white/90 border border-white/10">
                  <p className="text-sm">
                    We use this list to plan themed speed-dating nights and help you find others from similar backgrounds — it's never used to exclude anyone.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            {" "}— not a gate.
            If you want, message support and we'll take your region into account when planning future themed nights.
          </p>
        </div>
      </div>
    </div>
  );
}
